import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AppLoadingScreen from '../components/AppLoadingScreen';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  requiredRole?: string | null;
  redirectTo?: string;
  checkGlobalRole?: boolean;
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = null,
  redirectTo = '/',
  checkGlobalRole = false
}) => {
  const {
    user,
    loading,
    userLoaded,
    currentOrganization,
    organizations,
    organizationsLoaded,
    isEmailVerified,
    needsOrganizationSetup
  } = useAuth();
  const location = useLocation();

  if (loading || !userLoaded) {
    return <AppLoadingScreen />;
  }

  if (userLoaded && !user) {
    return <Navigate to="/login" replace />;
  }

  if (user?.user_metadata?.needs_password) {
    return <Navigate to="/password" replace />;
  }

  // Check email verification
  if (user && !isEmailVerified()) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check if user needs organization setup
  if (user && organizationsLoaded && needsOrganizationSetup()) {
    return <Navigate to="/organization-setup" replace />;
  }

  // Smart routing for SaaS admins
  const isSaasAdmin = user?.user_metadata?.global_role === 'saasadmin';
  const hasOrganizations = organizations && organizations.length > 0;
  const isAdminRoute = location.pathname.startsWith('/admin');

  // If SaaS admin has no organizations and tries to access regular routes, redirect to admin
  if (isSaasAdmin && !hasOrganizations && organizationsLoaded && !isAdminRoute) {
    return <Navigate to="/admin" replace />;
  }

  // Role-based access control
  if (requiredRole) {
    if (checkGlobalRole) {
      // Check for global roles (like saasadmin)
      const hasGlobalRole =
        user?.user_metadata?.global_role === requiredRole ||
        organizations.some(org => org.role === requiredRole);

      if (!hasGlobalRole) {
        return <Navigate to={redirectTo} replace />;
      }
    } else {
      // Check for organization-specific role
      if (!currentOrganization || currentOrganization.role !== requiredRole) {
        return <Navigate to={redirectTo} replace />;
      }
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
